import React, { useState } from 'react';
import CreatividadNombre from './CreatividadNombre';
import styles from '../../styles/detalleRespuestaModal.module.css';

const CREATIVIDAD_COLORS = [
  { bar: '#e1147e', soft: 'rgba(225, 20, 126, 0.12)', ring: 'rgba(225, 20, 126, 0.35)' },
  { bar: '#6366f1', soft: 'rgba(99, 102, 241, 0.12)', ring: 'rgba(99, 102, 241, 0.35)' },
  { bar: '#0ea5e9', soft: 'rgba(14, 165, 233, 0.12)', ring: 'rgba(14, 165, 233, 0.35)' },
  { bar: '#10b981', soft: 'rgba(16, 185, 129, 0.12)', ring: 'rgba(16, 185, 129, 0.35)' },
];

const ESTADO_ENCUESTA = {
  programada: 'Programada',
  activa: 'Activa',
  finalizada: 'Finalizada',
};

function tienePercentil(c) {
  return c != null && Number.isFinite(Number(c.percentil));
}

function valorPercentil(c) {
  return tienePercentil(c) ? Math.round(Number(c.percentil)) : null;
}

function pickPrincipal(creatividades) {
  if (!creatividades.length) return null;
  if (creatividades.some(tienePercentil)) {
    return [...creatividades].sort(
      (a, b) =>
        (Number(b.percentil) || 0) - (Number(a.percentil) || 0) ||
        b.porcentaje - a.porcentaje ||
        b.puntos - a.puntos
    )[0];
  }
  return creatividades.find((c) => c.es_principal) ?? creatividades[0];
}

function MetricasCreatividad({ creatividad, color, variant = 'card' }) {
  const percentil = valorPercentil(creatividad);
  const porcentaje = Number.isFinite(Number(creatividad?.porcentaje))
    ? Math.round(Number(creatividad.porcentaje))
    : null;
  const usaPercentil = percentil != null;

  if (variant === 'hero') {
    return (
      <div className={styles.heroPctWrap}>
        <span className={styles.heroPct} style={{ color }}>
          {usaPercentil ? `P${percentil}` : `${porcentaje ?? 0}%`}
        </span>
        <span className={styles.heroPctLabel}>
          {usaPercentil ? 'percentil' : 'de tu perfil'}
        </span>
        {usaPercentil && porcentaje != null ? (
          <span className={styles.heroPctSecondary}>{porcentaje}% del perfil</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.cardMetrics}>
      <span className={styles.cardPct} style={{ color }}>
        {usaPercentil ? `P${percentil}` : `${porcentaje ?? 0}%`}
      </span>
      {usaPercentil && porcentaje != null ? (
        <span className={styles.cardPctSecondary}>{porcentaje}%</span>
      ) : null}
    </div>
  );
}

const DetalleRespuestaModal = ({
  detalle,
  loading,
  onClose,
  onExportarInforme,
  exportandoInforme = false,
}) => {
  const [mostrarPreguntas, setMostrarPreguntas] = useState(true);

  if (loading) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <p className={styles.loadingText}>Cargando perfil creativo…</p>
        </div>
      </div>
    );
  }

  if (!detalle) return null;

  const creatividades = [...(detalle.creatividades || [])].sort((a, b) => {
    const pa = Number(a.percentil);
    const pb = Number(b.percentil);
    if (Number.isFinite(pb) || Number.isFinite(pa)) {
      return (pb || 0) - (pa || 0) || b.porcentaje - a.porcentaje || b.puntos - a.puntos;
    }
    return b.porcentaje - a.porcentaje || b.puntos - a.puntos;
  });
  const tienePerfil = creatividades.some((c) => (c.puntos ?? 0) > 0);
  const principal = tienePerfil ? pickPrincipal(creatividades) : null;
  const restantes = creatividades.filter((c) => c !== principal);
  const pctCompletado = detalle.total_preguntas
    ? Math.round(((detalle.respondidas ?? 0) / detalle.total_preguntas) * 100)
    : 0;

  const colorFor = (index, isMain) =>
    CREATIVIDAD_COLORS[isMain ? 0 : (index % CREATIVIDAD_COLORS.length)];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.topBar}>
          <div className={styles.topBarInfo}>
            <p className={styles.eyebrow}>Perfil de creatividades</p>
            <h2 className={styles.userName}>{detalle.usuario?.nombre_completo ?? '—'}</h2>
            {detalle.usuario?.email ? (
              <p className={styles.userEmail}>{detalle.usuario.email}</p>
            ) : null}
            <div className={styles.metaRow}>
              <span>
                {detalle.respondidas ?? 0} / {detalle.total_preguntas ?? 40} preguntas
              </span>
              <span className={styles.metaDot} aria-hidden>·</span>
              <span>{pctCompletado}% del test</span>
              {detalle.encuesta?.estado && (
                <>
                  <span className={styles.metaDot} aria-hidden>·</span>
                  <span>
                    Encuesta {ESTADO_ENCUESTA[detalle.encuesta.estado] || detalle.encuesta.estado}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className={styles.topBarActions}>
            <button
              type="button"
              className={styles.exportBtn}
              onClick={onExportarInforme}
              disabled={exportandoInforme || (detalle.respondidas ?? 0) === 0}
              title={
                (detalle.respondidas ?? 0) === 0
                  ? 'El participante no tiene respuestas; no se puede generar el informe'
                  : 'Descargar informe PDF'
              }
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <polyline points="9 15 12 18 15 15" />
              </svg>
              {exportandoInforme ? 'Generando…' : 'Exportar informe'}
            </button>
          </div>
        </header>

        <div className={styles.body}>
          {!tienePerfil ? (
            <p className={styles.emptyCreatividades}>
              Aún no hay suficientes respuestas para calcular el perfil de creatividades.
            </p>
          ) : (
            <>
              {principal && (
                <section className={styles.heroSection}>
                  <div
                    className={styles.heroCard}
                    style={{
                      borderColor: colorFor(0, true).ring,
                      background: `linear-gradient(135deg, ${colorFor(0, true).soft} 0%, var(--bg-surface) 55%)`,
                    }}
                  >
                    <div className={styles.heroBadge}>Creatividad principal</div>
                    <div className={styles.heroContent}>
                      <div className={styles.heroLeft}>
                        <CreatividadNombre creatividad={principal} variant="hero" />
                        {principal.descripcion && (
                          <p className={styles.heroDesc}>{principal.descripcion}</p>
                        )}
                      </div>
                      <MetricasCreatividad
                        creatividad={principal}
                        color={colorFor(0, true).bar}
                        variant="hero"
                      />
                    </div>
                    <div className={styles.heroBarBg}>
                      <div
                        className={styles.heroBarFill}
                        style={{
                          width: `${valorPercentil(principal) ?? principal.porcentaje}%`,
                          backgroundColor: colorFor(0, true).bar,
                        }}
                      />
                    </div>
                  </div>
                </section>
              )}

              {restantes.length > 0 && (
                <section className={styles.gridSection}>
                  <h4 className={styles.gridTitle}>Resto de creatividades</h4>
                  <div className={styles.creatividadesGrid}>
                    {restantes.map((c, i) => {
                      const col = colorFor(i + 1, false);
                      return (
                        <article
                          key={c.id_creatividad}
                          className={styles.creatividadCard}
                          style={{ borderColor: col.ring, backgroundColor: col.soft }}
                        >
                          <div className={styles.cardHeader}>
                            <CreatividadNombre creatividad={c} variant="card" />
                            <MetricasCreatividad creatividad={c} color={col.bar} />
                          </div>
                          <div className={styles.cardBarBg}>
                            <div
                              className={styles.cardBarFill}
                              style={{
                                width: `${valorPercentil(c) ?? c.porcentaje}%`,
                                backgroundColor: col.bar,
                              }}
                            />
                          </div>
                          {c.descripcion && (
                            <p className={styles.cardDesc}>{c.descripcion}</p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}

          <section className={styles.preguntasSection}>
            <button
              type="button"
              className={styles.preguntasToggle}
              onClick={() => setMostrarPreguntas((v) => !v)}
              aria-expanded={mostrarPreguntas}
            >
              <span>Respuestas por pregunta</span>
              <span className={styles.toggleIcon}>{mostrarPreguntas ? '▾' : '▸'}</span>
            </button>
            {mostrarPreguntas && (
              <div className={styles.preguntasTableWrap}>
                <table className={styles.preguntasTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Pregunta</th>
                      <th>Valor (0-6)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalle.respuestas || []).map((r, i) => (
                      <tr key={r.id_pregunta} className={r.valor == null ? styles.rowEmpty : ''}>
                        <td>{i + 1}</td>
                        <td>{r.texto_es ?? '—'}</td>
                        <td>
                          <span className={r.valor != null ? styles.valorBadge : styles.valorEmpty}>
                            {r.valor != null ? r.valor : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <footer className={styles.footer}>
          <button type="button" className={styles.footerCloseBtn} onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DetalleRespuestaModal;
